import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Button, Typography, Divider, CircularProgress } from '@mui/material';
import { AppDispatch } from '../reduxStore/Store';
import {
  fetchSituationPatrimoniale,
  clearSituationPatrimoniale,
} from '../reduxStore/StatistiqueSlice';
import { useSnackbar } from './SnackbarProvider';
import AppSurface from './layout/AppSurface';
import { usePermission } from '../hooks/usePermission';
import { PERMISSION_PRIX_REVIENT } from '../constants/enums';

const today = () => new Date().toLocaleDateString('sv-SE');
const fmt = (n: number) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Une ligne "libellé ........ montant".
const Ligne: React.FC<{ label: string; montant: number; bold?: boolean }> = ({ label, montant, bold }) => (
  <Box display="flex" justifyContent="space-between" sx={{ py: 0.4, fontWeight: bold ? 700 : 400 }}>
    <span>{label}</span>
    <span>{fmt(montant)}</span>
  </Box>
);

/**
 * Situation patrimoniale — panneau embarquable (onglet de Stock & Statistiques).
 * Actif (caisse + stock + créances) − dettes fournisseurs = situation nette.
 */
const SituationPatrimonialePanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { data, loading, error } = useSelector((state: any) => state.StatistiqueSlice.situationPatrimoniale);
  // Sans le droit de voir le coût (0156) : stock au coût, total actif et situation nette valent null
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);

  const [date, setDate] = useState(today());

  const charger = () => {
    dispatch(fetchSituationPatrimoniale({ date }));
  };

  useEffect(() => {
    charger();
    return () => { dispatch(clearSituationPatrimoniale()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showSnackbar(error, 'error');
  }, [error, showSnackbar]);

  return (
    <>
      {/* ── Filtre date ── */}
      <AppSurface sx={{ mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            type="date"
            label="À la date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={charger} disabled={loading}>
            Calculer
          </Button>
        </Box>
      </AppSurface>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          {/* ── Ce que je possède ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Ce que je possède</Typography>
            <Divider sx={{ mb: 1 }} />
            <Ligne label="Trésorerie (caisse)" montant={data.solde_caisse} />
            {voitCout && <Ligne label="Stock (valorisé au coût)" montant={data.valeur_stock} />}
            <Ligne label="Immobilisations (VNC)" montant={data.valeur_immobilisations ?? 0} />
            <Ligne label="Créances clients" montant={data.creances_clients} />
            {voitCout ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Ligne label="Total actif" montant={data.total_actif} bold />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Stock, total actif et situation nette non affichés : ils dépendent du prix de revient.
              </Typography>
            )}
          </AppSurface>

          {/* ── Ce que je dois ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Ce que je dois</Typography>
            <Divider sx={{ mb: 1 }} />
            <Ligne label="Dettes fournisseurs" montant={data.dettes_fournisseurs} />
          </AppSurface>

          {/* ── Situation nette ── */}
          {voitCout && <AppSurface>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={{
                fontWeight: 800,
                fontSize: 18,
                color: data.situation_nette >= 0 ? 'success.main' : 'error.main',
              }}
            >
              <span>Situation nette</span>
              <span>{fmt(data.situation_nette)}</span>
            </Box>
          </AppSurface>}
        </>
      )}
    </>
  );
};

export default SituationPatrimonialePanel;
